/**
 * Role → capability matrix. MIRROR of backend/permissions.py — keep in sync.
 * The backend is the enforcement point; this only decides what to show.
 */
const SA = "super_admin", CA = "company_admin", PM = "project_manager", PROC = "procurement",
  ACC = "accountant", SP = "sales_person", FE = "field_engineer", MV = "management_view", CL = "client";
const ALL_STAFF = [SA, CA, PM, PROC, ACC, SP, FE, MV];

export const ROLE_LABELS = {
  super_admin: "Super Admin",
  company_admin: "Company Admin",
  project_manager: "Project Manager",
  procurement: "Procurement",
  accountant: "Accountant",
  sales_person: "Sales Person",
  field_engineer: "Field Engineer",
  management_view: "Management (read-only)",
  client: "Client (read-only)",
};

// Roles a workspace admin can assign (super_admin is platform-only)
export const ASSIGNABLE_ROLES = [
  { value: "company_admin", label: "Company Admin", hint: "Everything in the workspace" },
  { value: "project_manager", label: "Project Manager", hint: "Projects, tasks, POs, PIs, payments" },
  { value: "procurement", label: "Procurement", hint: "POs, MRNs, vendors, vendor payments" },
  { value: "accountant", label: "Accountant", hint: "PIs, POs, all payments, expenses, cash position, billing" },
  { value: "sales_person", label: "Sales Person", hint: "CRM and solar design" },
  { value: "field_engineer", label: "Field Engineer", hint: "Tasks, DPRs, MRNs on assigned projects — no money" },
  { value: "management_view", label: "Management View", hint: "Read-only across the workspace" },
  { value: "client", label: "Client (Read-Only)", hint: "Their projects; shared documents and invoices only" },
];

const CAPS = {
  "projects.view": [...ALL_STAFF, CL],
  "projects.edit": [SA, CA, PM],
  "projects.delete": [SA, CA],
  "projects.value.view": [SA, CA, PM, ACC, MV, SP],   // contract value / portfolio ₹ (never field engineers, procurement, clients)
  "tasks.edit": [SA, CA, PM, FE],
  "tasks.manage": [SA, CA, PM],
  "documents.edit": [SA, CA, PM, FE, PROC],
  "documents.delete": [SA, CA, PM, PROC],
  "dpr.edit": [SA, CA, PM, FE],
  "dpr.delete": [SA, CA, PM],
  "mrn.view": ALL_STAFF,
  "mrn.edit": [SA, CA, PM, FE, PROC],
  "mrn.delete": [SA, CA, PM, PROC],
  "documents.view": [...ALL_STAFF, CL],
  "bank_report.view": ALL_STAFF,
  "bank_report.edit": [SA, CA, PM, SP, ACC],
  "bill_audit.view": ALL_STAFF,
  "bill_audit.edit": [SA, CA, PM, SP, ACC],
  "solar_design.edit": [SA, CA, PM, SP, FE],
  "po.view": [SA, CA, PM, PROC, ACC, MV],
  "po.edit": [SA, CA, PM, PROC],
  "po.void": [SA, CA, PM, PROC],
  "pi.view": [SA, CA, PM, ACC, MV],
  "pi.edit": [SA, CA, PM, ACC],
  "pi.void": [SA, CA, PM, ACC],
  "client_payments.view": [SA, CA, PM, ACC, MV],
  "client_payments.edit": [SA, CA, PM, ACC],
  "vendor_payments.view": [SA, CA, PM, PROC, ACC, MV],
  "vendor_payments.edit": [SA, CA, PM, PROC, ACC],
  "expenses.view": [SA, CA, PM, ACC, MV],
  "expenses.edit": [SA, CA, PM, ACC],
  "cash_position.view": [SA, CA, PM, ACC, MV],
  "reports.view": [SA, CA, PM, ACC, MV],
  "receipts.send": [SA, CA, PM, ACC],
  "vendors.view": ALL_STAFF,
  "vendors.edit": [SA, CA, PM, PROC],
  "vendors.delete": [SA, CA, PM, PROC],
  "clients.view": ALL_STAFF,
  "clients.edit": [SA, CA, PM, SP, ACC],
  "clients.delete": [SA, CA],
  "items.view": ALL_STAFF,
  "items.edit": [SA, CA, PM, PROC, ACC],
  "crm.view": [SA, CA, PM, SP, MV],
  "crm.edit": [SA, CA, PM, SP],
  "crm.convert": [SA, CA, PM, SP],
  "crm.reminders": [SA, CA, PM],
  "users.manage": [SA, CA],
  "workspace.edit": [SA, CA],
  "billing.manage": [SA, CA, ACC],
  "mcp.tokens": [SA, CA, PM, SP, ACC, PROC],
  // pages
  "page.dashboard": ALL_STAFF,
  "page.my_tasks": ALL_STAFF,
  "page.solar_design": [SA, CA, PM, SP, FE, MV],
  "page.pulse": ALL_STAFF,
  "page.bill_audit": ALL_STAFF,
  "page.team": [SA, CA, PM, MV, ACC, PROC, SP, FE],
  "page.masters": [SA, CA, PM, PROC, ACC, MV, SP],
  "page.settings": ALL_STAFF,
};

export function can(user, cap) {
  const role = user?.role;
  if (!role) return false;
  if (role === SA) return true;
  return (CAPS[cap] || []).includes(role);
}

// Any money-related page: payments hub
export const canSeePayments = (user) =>
  can(user, "client_payments.view") || can(user, "vendor_payments.view") || can(user, "expenses.view");
